import { useStrings } from "@1js/localization-components";
import type { Prompt } from "@1js/pl-card-components";
import { PromptCard } from "@1js/pl-card-components";
import { Text, makeStyles, shorthands } from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { DetailMoreLikeThis } from "./MoreLikeThis.web.string";

const useStyles = makeStyles({
  container: {
    paddingTop: "29px",
  },

  promptsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    alignSelf: "stretch",
    paddingBottom: "3rem",
    ...shorthands.gap("0.75rem"),
    "@media (max-width: 540px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      ...shorthands.gap("1rem"),
    },
  },

  titleContainer: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    paddingBottom: "0.75rem",
    ...shorthands.gap("0.75rem"),
    "@media (max-width: 540px)": {
      alignItems: "center",
      ...shorthands.padding("0.5rem 0rem"),
    },
  },

  title: {
    color:
      "var(--light-foreground-foreground-1, var(--color-neutral-foreground-1-rest, #242424))",
    fontSize: "1.5rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "-0.045rem",
    "@media (max-width: 540px)": {
      fontSize: "1.25rem",
      letterSpacing: "-0.0375rem",
    },
  },

  overrideCard: {
    width: "100%",
  },
});

type Props = {
  prompts: Prompt[];
  onClicked?: (prompt: Prompt) => void;
  isBookmarkEnabled?: boolean;
  onBookmarkToggled?: (prompt: Prompt, value: boolean) => Promise<void>;
};

export const MoreLikeThis: React.FC<Props> = ({
  prompts,
  onClicked,
  isBookmarkEnabled,
  onBookmarkToggled,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    DetailMoreLikeThis,
  });
  const { isFlightActive } = useFlightContext();
  const appearance = isFlightActive("isGoBoldPromptCardEnabled")
    ? "web-v2"
    : "web";

  if (!prompts || prompts.length === 0) return null;
  return (
    <div className={styles.container} data-testid="morelikethis">
      <div className={styles.titleContainer}>
        <Text className={styles.title}>
          {localizations.DetailMoreLikeThis()}
        </Text>
      </div>
      <div className={styles.promptsContainer} role="list" aria-label="Prompt">
        {prompts.map((p, idx) => (
          <PromptCard
            key={p.Id}
            appearance={appearance}
            prompt={p}
            onClicked={onClicked}
            isFavoriteEnabled={isBookmarkEnabled}
            onFavoriteToggled={onBookmarkToggled}
            className={styles.overrideCard}
            aria-posinset={idx + 1}
            displayPosition={0} //setting to 0 for all items in the list to be tabbable.
            aria-setsize={prompts.length}
            isShowPromptAuthor={true}
            isBadgesEnabled={true}
          />
        ))}
      </div>
    </div>
  );
};
